import {
  CompletedPart,
  CompleteMultipartUploadCommand,
  CompleteMultipartUploadCommandInput,
  CompleteMultipartUploadCommandOutput,
  CreateMultipartUploadCommand,
  CreateMultipartUploadCommandInput,
  CreateMultipartUploadCommandOutput,
  PutObjectRequest,
  S3Client,
  UploadPartCommand,
  UploadPartCommandInput,
} from '@aws-sdk/client-s3';

/**
 * Maximum number of parts allowed by S3.
 */
const MAX_PARTS = 10000;
/**
 * Minimum part size allowed by S3.
 */
const MIN_PART_SIZE = 5 * 1024 * 1024;

interface FileChunk {
  PartNumber: number;
  BodyChunk: Blob;
}

/**
 * Generator function that returns sequential chunks of a Blob given the Blob and a chunk size.
 * @private
 */
function* __chunker(blob: Blob, chunkSize: number): Generator<FileChunk, void> {
  let partNumber = 1;
  let offset = 0;
  while (offset < blob.size) {
    const end = Math.min(offset + chunkSize, blob.size);
    const part = blob.slice(offset, end);
    yield { PartNumber: partNumber, BodyChunk: part };
    offset = end;
    partNumber++;
  }
}

/**
 * Creates a multipart upload.
 * @private
 */
const __createMultipartUpload = ({
  client,
  params,
  retries,
}: {
  client: S3Client;
  params: CreateMultipartUploadCommandInput;
  retries: number;
}): Promise<CreateMultipartUploadCommandOutput> =>
  client.send(new CreateMultipartUploadCommand(params)).catch((err) => {
    if (retries > 0) {
      return __createMultipartUpload({ client, params, retries: retries - 1 });
    } else {
      throw err;
    }
  });

/**
 * Uploads a file part to S3.
 * @private
 */
const __uploadPart = ({
  client,
  params,
  retries,
}: {
  client: S3Client;
  params: UploadPartCommandInput;
  retries: number;
}): Promise<CompletedPart> =>
  client
    .send(new UploadPartCommand(params))
    .catch((err) => {
      if (retries > 0) {
        return __uploadPart({ client, params, retries: retries - 1 });
      } else {
        throw err;
      }
    })
    .then((uploadPartResponse) => ({
      PartNumber: params.PartNumber,
      ETag: uploadPartResponse.ETag,
    }));

/**
 * Uploads file parts while generator has data.
 * @private
 */
const __uploadFileParts = async ({
  client,
  generator,
  retries,
  params,
}: {
  client: S3Client;
  generator: Generator<FileChunk, void>;
  retries: number;
  params: Pick<UploadPartCommandInput, 'Bucket' | 'Key' | 'UploadId'>;
}): Promise<Array<CompletedPart>> => {
  const parts: Array<CompletedPart> = [];
  for (const chunk of generator) {
    const uploadParams: UploadPartCommandInput = {
      ...params,
      PartNumber: chunk.PartNumber,
      Body: chunk.BodyChunk,
    };
    parts.push(await __uploadPart({ client, retries, params: uploadParams }));
  }
  return parts;
};

/**
 * Completes a multipart upload.
 * @private
 */
const __completeMultipartUpload = ({
  client,
  params,
  retries,
}: {
  client: S3Client;
  params: CompleteMultipartUploadCommandInput;
  retries: number;
}): Promise<CompleteMultipartUploadCommandOutput> =>
  client.send(new CompleteMultipartUploadCommand(params)).catch((err) => {
    if (retries > 0) {
      return __completeMultipartUpload({ client, params, retries: retries - 1 });
    } else {
      throw err;
    }
  });

/**
 * Uploads a file to S3 in a multipart upload.
 */
export const uploadMultipartFile = async ({
  client,
  params,
  chunkSize = MIN_PART_SIZE,
  queueSize,
  retries,
}: {
  client: S3Client;
  params: PutObjectRequest;
  /**
   * The size of each part in bytes.
   * @default 5 MB
   */
  chunkSize?: number;
  /**
   * The number of concurrent uploads to make.
   */
  queueSize: number;
  /**
   * Number of times to retry the upload if the request fails.
   */
  retries: number;
}): Promise<CompleteMultipartUploadCommandOutput> => {
  const { Bucket, Key, Body } = params;
  if (!(Body instanceof Blob)) {
    throw new Error('Body must be a Blob');
  }
  if (chunkSize < MIN_PART_SIZE) {
    throw new Error('Chunk size must be at least 5 MB');
  }
  if (queueSize < 1) {
    throw new Error('Queue size must be at least 1');
  }
  const { UploadId } = await __createMultipartUpload({ client, params: { Bucket, Key }, retries });

  // Detect too many chunks
  const numParts = Math.ceil(Body.size / chunkSize);
  if (numParts > MAX_PARTS) {
    throw new Error(
      `Exceeded ${MAX_PARTS} parts of the upload to ${params.Key} and ${params.Bucket} in ${chunkSize}MB chunks.`
    );
  }

  const uploadingQueues: Array<Promise<Array<CompletedPart>>> = [];
  // Launch a queue of uploads
  const generator = __chunker(Body, chunkSize);
  for (let i = 0; i < queueSize; ++i) {
    uploadingQueues.push(
      __uploadFileParts({
        client,
        generator,
        retries,
        params: {
          Bucket,
          Key,
          UploadId,
        },
      })
    );
  }
  // Join part uploads
  const Parts = (await Promise.all(uploadingQueues)).flat();
  // Sort uploaded parts by part number
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  // tslint:disable-next-line: no-non-null-assertion
  Parts.sort((a, b) => a.PartNumber! - b.PartNumber!);
  const completeParams: CompleteMultipartUploadCommandInput = {
    Bucket,
    Key,
    UploadId,
    MultipartUpload: { Parts },
  };
  return __completeMultipartUpload({ client, params: completeParams, retries });
};

/**
 * Patches the S3Client flow to fix the uploadMultipartFile method until the AWS SDK is fixed.
 *
 * Related issues:
 *    https://github.com/aws/aws-sdk-js-v3/issues/1814
 *    https://github.com/aws/aws-sdk-js-v3/issues/2144
 *    https://github.com/aws/aws-sdk-js-v3/issues/2445
 *
 * PR: https://github.com/aws/aws-sdk-js-v3/pull/2434
 */
export const patchS3CompleteMultipartUpload = (client: S3Client): void => {
  client.middlewareStack.add(
    (next, _context) => (args: any) => {
      if ('string' === typeof args.request?.body && args.request.body.includes('CompletedMultipartUpload')) {
        args.request.body = args.request.body.replace(/CompletedMultipartUpload/g, 'CompleteMultipartUpload');
      }
      return next(args);
    },
    {
      step: 'build',
      priority: 'high',
    }
  );
};
